import { BedDouble, Ruler, Sofa } from 'lucide-react'
import Link from 'next/link'
import React from 'react'

import { getApartmentHref } from '@/lib/apartments/apartmentPaths'
import { resolveApartmentRentDisplayCopy } from '@/lib/apartments/apartmentRentDisplayCopy'
import type { RoomTypeListingItem } from '@/lib/apartments/apartmentsQuery'
import { buildPopularChoiceBannerProps } from '@/lib/apartments/popularChoiceViews'
import { formatRoomCountLabel } from '@/lib/apartments/roomCountLabels'
import {
  getListingDisplayRoomCount,
  getListingFurnishingPreference,
  getListingOutdoorOptions,
} from '@/lib/apartments/roomTypesFilters'
import {
  PopularChoiceBanner,
  type PopularChoiceBannerProps,
} from '@/components/Room/PopularChoiceBanner'
import { PromotionAvailableBadge } from '@/components/Promotions/PromotionAvailableBadge'
import { PromotionLocationBadge } from '@/components/Promotions/PromotionLocationBadge'
import type { Locale } from '@/lib/i18n/config'
import { formatPriceNumber } from '@/lib/pricing/formatPriceNumber'
import { cn } from '@/utilities/ui'

const AVAILABLE_WITH_ICON = '/media/room-types/available-with-icon.svg'

function CardMetaItem({
  children,
  className,
  icon,
}: {
  children: React.ReactNode
  className?: string
  icon: React.ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-start gap-1 font-outfit text-base leading-5 text-dark-800/80',
        className,
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0 break-words">{children}</span>
    </span>
  )
}

export type RoomTypeCardCopy = {
  allInclusiveRentLabel: string
  availableWithBalcony: string
  availableWithBalconyOrPatio: string
  availableWithPatio: string
  furnishedLabelFullyEquipped: string
  furnishedLabelFullyFurnished: string
  imageAltFallback: string
  popularChoiceBody: string
  popularChoiceTitle: string
  priceAmount: string
  promotionAvailable: string
  rentTermLabel: string
  roomCountLabel?: string
  roomCountLabelDouble?: string
  roomCountLabelOne?: string
  sizeLabel: string
  sizeLabelRange: string
  startingAtLabel: string
  viewDetails: string
  viewDetailsAriaLabel: string
}

export type RoomTypeCardPopularChoice = PopularChoiceBannerProps

export type RoomTypeCardProps = {
  allInclusiveRentLabel: string
  /** Outdoor variant line from Figma #1926:8731; omit/null when none. */
  availableWithLabel?: string | null
  cta: {
    ariaLabel: string
    href: string
    label: string
  }
  furnishedLabel?: string | null
  imageAlt: string
  imageSrc: string
  listItem?: boolean
  locationLabel: string
  /** Figma Message banner (#1926:9119); omit/null to hide. */
  popularChoice?: RoomTypeCardPopularChoice | null
  priceAmount: string
  promotionAvailable?: boolean
  promotionAvailableLabel?: string
  rentTermLabel: string
  roomCountLabel?: string | null
  sizeLabel: string
  startingAtLabel: string
  title: string
}

function resolveAvailableWithLabel(
  listing: RoomTypeListingItem,
  copy: Pick<
    RoomTypeCardCopy,
    'availableWithBalcony' | 'availableWithBalconyOrPatio' | 'availableWithPatio'
  >,
): string | null {
  const outdoorOptions = getListingOutdoorOptions(listing)
  const hasBalcony = outdoorOptions.includes('balcony')
  const hasPatio = outdoorOptions.includes('patio')

  if (hasBalcony && hasPatio) return copy.availableWithBalconyOrPatio
  if (hasBalcony) return copy.availableWithBalcony
  if (hasPatio) return copy.availableWithPatio
  return null
}

function resolveFurnishedLabel(
  listing: RoomTypeListingItem,
  copy: Pick<RoomTypeCardCopy, 'furnishedLabelFullyEquipped' | 'furnishedLabelFullyFurnished'>,
): string | null {
  const preference = getListingFurnishingPreference(listing)

  if (preference === 'fully-furnished-equipped') {
    return copy.furnishedLabelFullyEquipped
  }

  if (preference === 'fully-furnished') {
    return copy.furnishedLabelFullyFurnished
  }

  return null
}

export function getRoomTypeCardProps(
  listing: RoomTypeListingItem,
  locale: Locale,
  copy: RoomTypeCardCopy,
  options?: { hasPromotion?: boolean },
): RoomTypeCardProps {
  const primaryImage = listing.images[0]
  const formattedPrice = formatPriceNumber(listing.startingPrice, locale)
  const sizeLabel =
    listing.sizeMax != null
      ? copy.sizeLabelRange
          .replace('{min}', String(listing.size))
          .replace('{max}', String(listing.sizeMax))
      : copy.sizeLabel.replace('{size}', String(listing.size))
  const displayRoomCount = getListingDisplayRoomCount(listing)
  const rentCopy = resolveApartmentRentDisplayCopy(listing, {
    rentPrimaryText: copy.allInclusiveRentLabel,
    rentSecondaryText: copy.rentTermLabel,
  })

  return {
    allInclusiveRentLabel: rentCopy.rentPrimaryText,
    availableWithLabel: resolveAvailableWithLabel(listing, copy),
    cta: {
      ariaLabel: copy.viewDetailsAriaLabel.replace('{title}', listing.name),
      href: getApartmentHref(
        listing.locationCitySlug,
        listing.locationSlug,
        listing.slug,
        locale,
      ),
      label: copy.viewDetails,
    },
    furnishedLabel: resolveFurnishedLabel(listing, copy),
    imageAlt: primaryImage?.alt?.trim() || copy.imageAltFallback.replace('{title}', listing.name),
    imageSrc: primaryImage?.src ?? '',
    locationLabel: listing.location,
    popularChoice: buildPopularChoiceBannerProps(listing.id, listing.popularChoice, copy),
    priceAmount: copy.priceAmount.replace('{price}', formattedPrice),
    promotionAvailable: options?.hasPromotion ?? false,
    promotionAvailableLabel: copy.promotionAvailable,
    rentTermLabel: rentCopy.rentSecondaryText,
    roomCountLabel: formatRoomCountLabel(displayRoomCount, copy),
    sizeLabel,
    startingAtLabel: copy.startingAtLabel,
    title: listing.name,
  }
}

export function RoomTypeCard({
  allInclusiveRentLabel,
  availableWithLabel = null,
  cta,
  furnishedLabel = null,
  imageAlt,
  imageSrc,
  listItem = false,
  locationLabel,
  popularChoice = null,
  priceAmount,
  promotionAvailable = false,
  promotionAvailableLabel,
  rentTermLabel,
  roomCountLabel = null,
  sizeLabel,
  startingAtLabel,
  title,
}: RoomTypeCardProps) {
  const card = (
    <Link
      aria-label={cta.ariaLabel}
      className={cn(
        'group flex h-full min-h-[33.8125rem] cursor-pointer flex-col gap-3 rounded-3xl border-[1.5px] border-black/10 bg-white/70 p-3 backdrop-blur-md transition-[box-shadow,background-color,border-color] hover:border-black/20 hover:bg-white/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
        listItem ? 'w-full max-w-none' : 'w-full max-w-[40rem] lg:max-w-[32rem]',
      )}
      href={cta.href}
    >
      <div className="w-full shrink-0 overflow-hidden">
        {imageSrc ? (
          <img
            alt={imageAlt}
            className="h-[16.5rem] w-full rounded-[10px] object-cover"
            src={imageSrc}
          />
        ) : (
          <div aria-hidden className="h-[16.5rem] w-full rounded-[10px] bg-black/5" />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-4 pt-3">
        <div className="flex w-full flex-row flex-wrap justify-between gap-2">
          <PromotionLocationBadge label={locationLabel} />
          {promotionAvailable && promotionAvailableLabel ? (
            <PromotionAvailableBadge label={promotionAvailableLabel} />
          ) : null}
        </div>

        <h3 className="font-display text-[1.75rem] font-medium leading-8 tracking-[-0.04em] text-dark-800/80">
          {title}
        </h3>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 items-start gap-x-6 gap-y-3">
            <CardMetaItem icon={<Ruler aria-hidden className="size-4" />}>{sizeLabel}</CardMetaItem>
            {roomCountLabel ? (
              <CardMetaItem
                className="justify-self-end"
                icon={<BedDouble aria-hidden className="size-4" />}
              >
                {roomCountLabel}
              </CardMetaItem>
            ) : null}
          </div>

          {availableWithLabel ? (
            <CardMetaItem
              className="w-full"
              icon={
                <img
                  alt=""
                  aria-hidden
                  className="size-4"
                  height={16}
                  src={AVAILABLE_WITH_ICON}
                  width={16}
                />
              }
            >
              {availableWithLabel}
            </CardMetaItem>
          ) : null}

          {furnishedLabel ? (
            <CardMetaItem className="w-full" icon={<Sofa aria-hidden className="size-4" />}>
              {furnishedLabel}
            </CardMetaItem>
          ) : null}
        </div>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-3">
            <p className="font-outfit text-sm font-medium leading-[14px] text-dark-800 underline decoration-wavy decoration-turquoise underline-offset-4">
              {allInclusiveRentLabel}
            </p>
            <p className="font-outfit text-sm leading-4 text-dark-800/80">{rentTermLabel}</p>
          </div>

          <div className="flex shrink-0 flex-col items-end justify-between gap-1">
            <p className="font-outfit text-sm leading-[14px] text-dark-800/80">{startingAtLabel}</p>
            <p className="font-outfit text-xl font-semibold leading-4 text-dark-800/80">
              {priceAmount}
            </p>
          </div>

          <span
            aria-hidden
            className="inline-flex h-12 w-full lg:w-fit shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-black/30 bg-white/30 px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors group-hover:bg-white/45 group-focus-visible:bg-white/45"
          >
            {cta.label}
          </span>
        </div>

        {popularChoice ? <PopularChoiceBanner {...popularChoice} /> : null}
      </div>
    </Link>
  )

  if (!listItem) {
    return card
  }

  return <li className="h-full min-w-0">{card}</li>
}
